import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { fullCalendarLocale } from '../utils/dateFormat';
import { MainLayout } from '../components/MainLayout';
import { MobileCalendarView } from '../components/MobileCalendarView';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus, Trash2, CheckCircle2, Clock,
  Users, FileText, Target, AlertTriangle, ExternalLink, Link2, Bell, User
} from 'lucide-react';
import { toast } from 'sonner';
import { CalendarSubscriptionDialog } from '../components/CalendarExport';
import { Switch } from '../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reminder time options in minutes
const REMINDER_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 5, label: '5 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' },
  { value: 1440, label: '1 day' },
];

const EVENT_COLORS = {
  task: { pending: '#f59e0b', completed: '#10b981' },
  followup: '#8b5cf6',
  quote_deadline: '#ef4444',
  enquiry_deadline: '#ec4899'
};

const CATEGORY_OPTIONS = [
  { id: 'outreach', label: 'Outreach', icon: Users },
  { id: 'follow_up', label: 'Follow-up', icon: Clock },
  { id: 'visibility', label: 'Visibility', icon: Target },
  { id: 'pipeline_hygiene', label: 'Pipeline', icon: FileText }
];

export const CalendarPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  
  const [events, setEvents] = useState([]);
  const [, setLoading] = useState(true); // Loading state tracked but UI doesn't show spinner
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  
  // Notification preferences state
  const [preferences, setPreferences] = useState({
    include_tasks: true,
    include_followups: true,
    include_quotes: true,
    include_enquiries: true,
    include_approvals: false,
    days_ahead: 30,
    reminder_minutes_tasks: 15,
    reminder_minutes_followups: 60,
    reminder_minutes_deadlines: 1440,
    reminder_minutes_approvals: 1440,
    reminder_minutes_milestones: 1440
  });
  const [savingPrefs, setSavingPrefs] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Duration options
  const DURATION_OPTIONS = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1.5 hours' },
    { value: 120, label: '2 hours' },
    { value: 0, label: 'Custom' }
  ];
  
  // Form state for creating/editing tasks
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    due_date: '',
    due_time: '',
    duration_minutes: 60,
    end_time: '',  // For custom duration
    all_day: true,
    category: 'outreach',
    status: 'pending'
  });

  // Calculate end time from start time and duration
  const calculateEndTime = (startTime, durationMinutes) => {
    if (!startTime || !durationMinutes) return '';
    const [hours, mins] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + durationMinutes;
    const endHours = Math.floor(totalMinutes / 60) % 24;
    const endMins = totalMinutes % 60;
    return `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
  };

  // Calculate duration from start and end time
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 60;
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const startMins = startH * 60 + startM;
    const endMins = endH * 60 + endM;
    const diff = endMins - startMins;
    return diff > 0 ? diff : 60;
  };

  // Update end time when start time or duration changes
  useEffect(() => {
    if (!formData.all_day && formData.due_time && formData.duration_minutes > 0) {
      const endTime = calculateEndTime(formData.due_time, formData.duration_minutes);
      if (endTime !== formData.end_time) {
        setFormData(prev => ({ ...prev, end_time: endTime }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.due_time, formData.duration_minutes, formData.all_day]);

  const fetchEvents = async (start, end) => {
    if (!token) return;
    setLoading(true);
    
    try {
      const res = await fetch(
        `${API_URL}/api/calendar/events?start=${start}&end=${end}&include_daily_tasks=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        // Transform events for FullCalendar
        const calendarEvents = data.events.map(event => ({
          id: event.id,
          title: event.title,
          start: event.start,
          end: event.end,
          allDay: event.allDay !== false,
          backgroundColor: event.type === 'task' 
            ? (event.status === 'completed' ? EVENT_COLORS.task.completed : EVENT_COLORS.task.pending)
            : event.type === 'daily_summary'
            ? event.color
            : event.type === 'outreach'
            ? '#06b6d4'
            : EVENT_COLORS[event.type] || '#6b7280',
          borderColor: 'transparent',
          extendedProps: {
            type: event.type,
            category: event.category,
            status: event.status,
            description: event.description,
            contact_id: event.contact_id,
            contact_name: event.contact_name,
            company_name: event.company_name,
            quote_id: event.quote_id,
            enquiry_id: event.enquiry_id,
            project_name: event.project_name,
            user_id: event.user_id,
            created_by: event.created_by,
            method: event.method,
            outcome: event.outcome,
            notes: event.notes,
            // Daily summary specific
            total_tasks: event.total_tasks,
            completed_tasks: event.completed_tasks,
            percentage: event.percentage,
            tasks: event.tasks,
            is_own: event.is_own,
            can_edit: event.can_edit,
            can_delete: event.can_delete
          }
        }));
        setEvents(calendarEvents);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
      toast.error('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  // Handle date range changes
  const handleDatesSet = (dateInfo) => {
    const start = dateInfo.startStr.split('T')[0];
    const end = dateInfo.endStr.split('T')[0];
    fetchEvents(start, end);
  };

  // Load notification preferences
  const loadPreferences = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/calendar/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPreferences(data.preferences);
      }
    } catch (err) {
      console.error('Failed to load calendar preferences:', err);
    }
  };

  // Save notification preferences
  const savePreferences = async () => {
    if (!token) return;
    setSavingPrefs(true);
    try {
      const res = await fetch(`${API_URL}/api/calendar/notification-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });
      if (res.ok) {
        toast.success('Notification preferences saved');
        setShowPreferencesModal(false);
        // Refresh calendar events to reflect preference changes
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
          const view = calendarApi.view;
          const start = view.activeStart.toISOString().split('T')[0];
          const end = view.activeEnd.toISOString().split('T')[0];
          fetchEvents(start, end);
        }
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  // Load preferences on mount
  useEffect(() => {
    loadPreferences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Handle clicking on an event
  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event;
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.startStr,
      ...event.extendedProps
    });
    
    // Populate form for editing - include time and duration fields
    const eventTime = event.startStr.includes('T') ? event.startStr.split('T')[1]?.substring(0, 5) : '';
    const isAllDay = event.allDay !== false && !eventTime;
    
    setFormData({
      title: event.title,
      description: event.extendedProps.description || '',
      due_date: event.startStr.split('T')[0],
      due_time: event.extendedProps.due_time || eventTime || '',
      duration_minutes: event.extendedProps.duration_minutes || 60,
      end_time: (event.extendedProps.due_time || eventTime) ? calculateEndTime(event.extendedProps.due_time || eventTime, event.extendedProps.duration_minutes || 60) : '',
      all_day: isAllDay,
      category: event.extendedProps.category || 'outreach',
      status: event.extendedProps.status || 'pending'
    });
    
    setShowEventModal(true);
  };

  // Handle clicking on a date to create new event
  const handleDateClick = (dateInfo) => {
    setFormData({
      title: '',
      description: '',
      due_date: dateInfo.dateStr,
      due_time: '',
      all_day: true,
      category: 'outreach',
      status: 'pending'
    });
    setShowCreateModal(true);
  };

  // Handle drag and drop to reschedule
  const handleEventDrop = async (dropInfo) => {
    const event = dropInfo.event;
    const newDate = dropInfo.event.startStr.split('T')[0];
    const eventType = event.extendedProps.type;
    
    try {
      let endpoint, body;
      
      if (eventType === 'task') {
        endpoint = `${API_URL}/api/calendar/tasks/${event.id}`;
        body = JSON.stringify({ due_date: newDate });
      } else if (eventType === 'followup') {
        const contactId = event.id.replace('followup-', '');
        endpoint = `${API_URL}/api/calendar/followups/${contactId}`;
        body = JSON.stringify({ new_date: newDate });
      } else {
        // Quote and enquiry deadlines are not directly editable via drag
        dropInfo.revert();
        toast.info('Edit this deadline from the enquiry/quote page');
        return;
      }
      
      const res = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body
      });
      
      if (res.ok) {
        toast.success('Rescheduled successfully');
      } else {
        dropInfo.revert();
        toast.error('Failed to reschedule');
      }
    } catch (err) {
      dropInfo.revert();
      toast.error('Error rescheduling event');
    }
  };

  // Refresh calendar events
  const refreshCalendar = () => {
    const calendarApi = calendarRef.current?.getApi();
    if (calendarApi) {
      const start = calendarApi.view.activeStart.toISOString().split('T')[0];
      const end = calendarApi.view.activeEnd.toISOString().split('T')[0];
      fetchEvents(start, end);
    }
  };

  // Create a new task
  const handleCreateTask = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    // Calculate duration for custom end time
    let durationMinutes = formData.duration_minutes;
    if (formData.duration_minutes === 0 && formData.due_time && formData.end_time) {
      durationMinutes = calculateDuration(formData.due_time, formData.end_time);
    }
    
    try {
      const res = await fetch(`${API_URL}/api/calendar/tasks`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          due_date: formData.due_date,
          due_time: formData.all_day ? null : formData.due_time,
          duration_minutes: formData.all_day ? null : durationMinutes,
          all_day: formData.all_day,
          category: formData.category
        })
      });
      
      if (res.ok) {
        toast.success('Task created');
        setShowCreateModal(false);
        // Refresh calendar
        try {
          refreshCalendar();
        } catch (refreshErr) {
          console.warn('Calendar refresh error:', refreshErr);
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to create task');
      }
    } catch (err) {
      console.error('Task creation error:', err);
      toast.error('Error creating task');
    }
  };

  // Update an existing task
  const handleUpdateTask = async () => {
    if (!selectedEvent || selectedEvent.type !== 'task') {
      toast.error('Can only edit tasks');
      return;
    }
    
    // Calculate duration for custom end time
    let durationMinutes = formData.duration_minutes;
    if (formData.duration_minutes === 0 && formData.due_time && formData.end_time) {
      durationMinutes = calculateDuration(formData.due_time, formData.end_time);
    }
    
    try {
      const res = await fetch(`${API_URL}/api/calendar/tasks/${selectedEvent.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          due_date: formData.due_date,
          due_time: formData.all_day ? null : formData.due_time,
          duration_minutes: formData.all_day ? null : durationMinutes,
          all_day: formData.all_day,
          category: formData.category,
          status: formData.status
        })
      });
      
      if (res.ok) {
        toast.success('Task updated');
        setShowEventModal(false);
        setSelectedEvent(null);
        try {
          refreshCalendar();
        } catch (refreshErr) {
          console.warn('Calendar refresh error:', refreshErr);
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to update task');
      }
    } catch (err) {
      console.error('Task update error:', err);
      toast.error('Error updating task');
    }
  };

  // Delete a task
  const handleDeleteTask = async () => {
    if (!selectedEvent || selectedEvent.type !== 'task') {
      toast.error('Can only delete tasks');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/today/tasks/${selectedEvent.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Task deleted');
        setShowEventModal(false);
        setSelectedEvent(null);
        try {
          refreshCalendar();
        } catch (refreshErr) {
          console.warn('Calendar refresh error:', refreshErr);
        }
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to delete task');
      }
    } catch (err) {
      console.error('Task delete error:', err);
      toast.error('Error deleting task');
    }
  };

  // Toggle task completion
  const handleToggleComplete = async () => {
    if (!selectedEvent || selectedEvent.type !== 'task') return;
    
    const newStatus = formData.status === 'completed' ? 'pending' : 'completed';
    setFormData({ ...formData, status: newStatus });
    
    try {
      const res = await fetch(`${API_URL}/api/calendar/tasks/${selectedEvent.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task uncompleted');
        try {
          refreshCalendar();
        } catch (refreshErr) {
          console.warn('Calendar refresh error:', refreshErr);
        }
      }
    } catch (err) {
      console.error('Task status update error:', err);
      toast.error('Error updating task');
    }
  };

  // Navigate to related item
  const handleNavigateToItem = () => {
    if (!selectedEvent) return;
    
    if (selectedEvent.type === 'followup' && selectedEvent.contact_id) {
      navigate(`/pipeline?contact=${selectedEvent.contact_id}`);
    } else if (selectedEvent.type === 'quote_deadline' && selectedEvent.enquiry_id) {
      navigate(`/enquiry/${selectedEvent.enquiry_id}/quote`);
    } else if (selectedEvent.type === 'enquiry_deadline' && selectedEvent.enquiry_id) {
      navigate(`/enquiry/${selectedEvent.enquiry_id}`);
    }
    
    setShowEventModal(false);
  };

  // Handle mobile event click - from MobileCalendarView
  const handleMobileEventClick = (event) => {
    setSelectedEvent({
      id: event.id,
      title: event.title,
      start: event.start,
      type: event.type,
      category: event.category,
      status: event.status,
      description: event.description,
      contact_id: event.contact_id,
      contact_name: event.contact_name,
      company_name: event.company_name,
      quote_id: event.quote_id,
      enquiry_id: event.enquiry_id,
      project_name: event.project_name,
      duration_minutes: event.duration_minutes,
      due_time: event.due_time,
      is_own: event.is_own,
      can_edit: event.can_edit,
      can_delete: event.can_delete,
      created_by: event.created_by
    });
    
    // Populate form for editing - include time and duration fields
    const eventTime = event.start.includes('T') ? event.start.split('T')[1]?.substring(0, 5) : '';
    const isAllDay = event.allDay !== false && !eventTime;
    
    setFormData({
      title: event.title,
      description: event.description || '',
      due_date: event.start.split('T')[0],
      due_time: eventTime || '',
      duration_minutes: event.duration_minutes || 60,
      end_time: eventTime ? calculateEndTime(eventTime, event.duration_minutes || 60) : '',
      all_day: isAllDay,
      category: event.category || 'outreach',
      status: event.status || 'pending'
    });
    
    setShowEventModal(true);
  };

  // Handle create task from mobile - accepts selected date from MobileCalendarView
  const handleMobileCreateTask = (selectedDateStr) => {
    setFormData({
      title: '',
      description: '',
      due_date: selectedDateStr || new Date().toISOString().split('T')[0],
      due_time: '',
      duration_minutes: 60,
      end_time: '',
      all_day: true,
      category: 'outreach',
      status: 'pending'
    });
    setShowCreateModal(true);
  };

  // Mobile Calendar View
  if (isMobile) {
    return (
      <MainLayout>
        <MobileCalendarView
          token={token}
          onEventClick={handleMobileEventClick}
          onCreateTask={handleMobileCreateTask}
          onOpenSubscription={() => setShowSubscriptionModal(true)}
          onOpenPreferences={() => setShowPreferencesModal(true)}
        />
        
        {/* Event Detail/Edit Modal - shared with desktop */}
        <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                {selectedEvent?.type === 'task' && <Target className="w-5 h-5 text-amber-500" />}
                {selectedEvent?.type === 'followup' && <Users className="w-5 h-5 text-violet-500" />}
                {selectedEvent?.type === 'quote_deadline' && <FileText className="w-5 h-5 text-red-500" />}
                {selectedEvent?.type === 'enquiry_deadline' && <AlertTriangle className="w-5 h-5 text-pink-500" />}
                {selectedEvent?.type === 'task' ? 'Edit Task' : 'Event Details'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {selectedEvent?.type === 'task' ? (
                <>
                  {/* Editable Task Form */}
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500">Title</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs text-slate-500">Description</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Date</Label>
                        <Input
                          type="date"
                          value={formData.due_date}
                          onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Category</Label>
                        <Select 
                          value={formData.category} 
                          onValueChange={(v) => setFormData({ ...formData, category: v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORY_OPTIONS.map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Time & Duration for editing */}
                    <div className="space-y-3 p-3 bg-slate-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="all-day-edit-mobile"
                          checked={formData.all_day}
                          onChange={(e) => setFormData({ ...formData, all_day: e.target.checked, due_time: '', duration_minutes: 60 })}
                          className="w-4 h-4 rounded border-slate-300 text-[#1e3a5f] focus:ring-[#1e3a5f]"
                        />
                        <label htmlFor="all-day-edit-mobile" className="text-sm text-slate-700 cursor-pointer">
                          All day task
                        </label>
                      </div>
                      
                      {!formData.all_day && (
                        <div className="space-y-3 pt-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-slate-500">Start</Label>
                              <Input
                                type="time"
                                value={formData.due_time}
                                onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">Duration</Label>
                              <Select 
                                value={String(formData.duration_minutes)} 
                                onValueChange={(v) => setFormData({ ...formData, duration_minutes: Number(v) })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Duration" />
                                </SelectTrigger>
                                <SelectContent>
                                  {DURATION_OPTIONS.filter(o => o.value !== 0).map(opt => (
                                    <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {formData.due_time && (
                            <div className="p-2 bg-blue-50 rounded-lg">
                              <p className="text-xs text-blue-700 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formData.due_time} — {calculateEndTime(formData.due_time, formData.duration_minutes)}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Status Toggle */}
                    <button
                      onClick={handleToggleComplete}
                      className={`w-full flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                        formData.status === 'completed'
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <CheckCircle2 className={`w-5 h-5 ${formData.status === 'completed' ? 'text-emerald-500' : 'text-slate-400'}`} />
                      {formData.status === 'completed' ? 'Completed' : 'Mark as Complete'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Read-only Event Info */}
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="font-medium text-slate-800">{selectedEvent?.title}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        {selectedEvent?.start && new Date(selectedEvent.start).toLocaleDateString('en-GB', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                      {/* Creator Attribution */}
                      {selectedEvent?.created_by && (
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          Created by {selectedEvent.created_by}
                        </p>
                      )}
                    </div>
                    
                    {selectedEvent?.type === 'followup' && (
                      <div className="p-3 bg-violet-50 rounded-xl">
                        <p className="text-sm text-violet-700">
                          <strong>Contact:</strong> {selectedEvent.contact_name}
                        </p>
                        {selectedEvent.company_name && (
                          <p className="text-sm text-violet-600 mt-1">
                            {selectedEvent.company_name}
                          </p>
                        )}
                      </div>
                    )}

                    {selectedEvent?.type === 'outreach' && (
                      <div className="p-3 bg-cyan-50 rounded-xl space-y-2">
                        <p className="text-sm text-cyan-700">
                          <strong>Method:</strong> {selectedEvent.method?.charAt(0).toUpperCase() + selectedEvent.method?.slice(1)}
                        </p>
                        {selectedEvent.contact_name && (
                          <p className="text-sm text-cyan-600">
                            <strong>Contact:</strong> {selectedEvent.contact_name}
                          </p>
                        )}
                        {selectedEvent.outcome && (
                          <p className="text-sm text-cyan-600">
                            <strong>Outcome:</strong> {selectedEvent.outcome}
                          </p>
                        )}
                        {selectedEvent.notes && (
                          <p className="text-sm text-cyan-600 mt-2">
                            {selectedEvent.notes}
                          </p>
                        )}
                      </div>
                    )}

                    {selectedEvent?.type === 'daily_summary' && (
                      <div className="p-3 bg-blue-50 rounded-xl space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-blue-700">Daily Progress</span>
                          <span className={`text-sm font-bold ${
                            selectedEvent.percentage === 100 ? 'text-emerald-600' : 'text-blue-600'
                          }`}>
                            {selectedEvent.completed_tasks}/{selectedEvent.total_tasks} ({selectedEvent.percentage}%)
                          </span>
                        </div>
                        {/* Progress bar */}
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              selectedEvent.percentage === 100 ? 'bg-emerald-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${selectedEvent.percentage}%` }}
                          />
                        </div>
                        {/* Task list - only show for user's own summary */}
                        {selectedEvent.is_own && selectedEvent.tasks && selectedEvent.tasks.length > 0 && (
                          <div className="space-y-1 pt-2 border-t border-blue-200">
                            <p className="text-xs text-blue-600 font-medium">Tasks:</p>
                            {selectedEvent.tasks.map((task, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs text-blue-700">
                                {task.status === 'completed' ? (
                                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                ) : (
                                  <div className="w-3 h-3 rounded-full border border-blue-400" />
                                )}
                                <span className={task.status === 'completed' ? 'line-through opacity-60' : ''}>
                                  {task.title}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Note for team member summaries */}
                        {!selectedEvent.is_own && (
                          <p className="text-xs text-blue-500 italic pt-2 border-t border-blue-200">
                            Task details are private to {selectedEvent.created_by}
                          </p>
                        )}
                      </div>
                    )}

                    {selectedEvent?.type === 'quote_deadline' && (
                      <div className="p-3 bg-red-50 rounded-xl">
                        <p className="text-sm text-red-700">
                          Quote expires on this date. Review and follow up if needed.
                        </p>
                      </div>
                    )}

                    {selectedEvent?.type === 'enquiry_deadline' && (
                      <div className="p-3 bg-pink-50 rounded-xl">
                        <p className="text-sm text-pink-700">
                          <strong>Project:</strong> {selectedEvent.project_name}
                        </p>
                      </div>
                    )}

                    {(selectedEvent?.type === 'followup' || selectedEvent?.type === 'quote_deadline' || selectedEvent?.type === 'enquiry_deadline') && (
                      <Button
                        onClick={handleNavigateToItem}
                        className="w-full bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    )}

                    {selectedEvent?.type === 'outreach' && selectedEvent?.contact_id && (
                      <Button
                        onClick={() => navigate(`/pipeline?contact=${selectedEvent.contact_id}`)}
                        className="w-full bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Contact
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>

            {selectedEvent?.type === 'task' && (selectedEvent?.can_edit || selectedEvent?.can_delete) && (
              <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
                {selectedEvent?.can_delete && (
                  <Button
                    variant="outline"
                    onClick={handleDeleteTask}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
                {selectedEvent?.can_edit && (
                  <Button
                    onClick={handleUpdateTask}
                    className="bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                  >
                    Save Changes
                  </Button>
                )}
              </DialogFooter>
            )}
            
            {selectedEvent?.type === 'task' && !selectedEvent?.can_edit && !selectedEvent?.can_delete && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <p>This task belongs to {selectedEvent?.created_by}. Only the owner or a Director can edit/delete it.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Task Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-md p-0 max-h-[85dvh] flex flex-col">
            <DialogHeader className="p-4 sm:p-6 pb-0 flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#1e3a5f]" />
                Create New Task
              </DialogTitle>
              <DialogDescription>
                {formData.due_date && `For ${new Date(formData.due_date).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}`}
              </DialogDescription>
            </DialogHeader>

            {/* Scrollable form content */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-2" style={{ minHeight: '200px', maxHeight: 'calc(85dvh - 180px)' }}>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter task title"
                    className="mt-1"
                    data-testid="new-task-title"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description"
                    className="mt-1"
                    rows={2}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Date</Label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Category</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Time-based scheduling with Duration */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <input
                      type="checkbox"
                      id="all-day-mobile"
                      checked={formData.all_day}
                      onChange={(e) => setFormData({ ...formData, all_day: e.target.checked, due_time: '', duration_minutes: 60, end_time: '' })}
                      className="w-4 h-4 rounded border-slate-300 text-[#1e3a5f] focus:ring-[#1e3a5f]"
                    />
                    <label htmlFor="all-day-mobile" className="text-sm text-slate-700 cursor-pointer">
                      All day task
                    </label>
                  </div>
                  
                  {!formData.all_day && (
                    <div className="space-y-3">
                      {/* Start Time */}
                      <div>
                        <Label className="text-xs text-slate-500">Start Time</Label>
                        <Input
                          type="time"
                          value={formData.due_time}
                          onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      
                      {/* Duration Selection */}
                      <div>
                        <Label className="text-xs text-slate-500">Duration</Label>
                        <div className="grid grid-cols-4 gap-2 mt-1">
                          {DURATION_OPTIONS.slice(0, 4).map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, duration_minutes: opt.value })}
                              className={`px-2 py-2 text-xs font-medium rounded-lg border transition-all ${
                                formData.duration_minutes === opt.value
                                  ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          {DURATION_OPTIONS.slice(4).map(opt => (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setFormData({ ...formData, duration_minutes: opt.value })}
                              className={`px-2 py-2 text-xs font-medium rounded-lg border transition-all ${
                                formData.duration_minutes === opt.value
                                  ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]'
                                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Custom End Time - only shown when Custom is selected */}
                      {formData.duration_minutes === 0 && (
                        <div>
                          <Label className="text-xs text-slate-500">End Time</Label>
                          <Input
                            type="time"
                            value={formData.end_time}
                            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                      )}
                      
                      {/* Calculated End Time Display */}
                      {formData.duration_minutes !== 0 && formData.due_time && (
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <p className="text-xs text-blue-700">
                            <Clock className="w-3 h-3 inline mr-1" />
                            Ends at {formData.end_time || calculateEndTime(formData.due_time, formData.duration_minutes)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Bottom padding to ensure content doesn't hide under sticky footer */}
                <div className="h-2" />
              </div>
            </div>

            {/* Sticky footer with CTA buttons */}
            <DialogFooter className="flex-shrink-0 p-4 sm:p-6 pt-3 border-t border-slate-100 bg-white gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTask}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                data-testid="create-task-submit"
              >
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notification Preferences Modal - shared with desktop */}
        <Dialog open={showPreferencesModal} onOpenChange={setShowPreferencesModal}>
          <DialogContent className="w-[95vw] max-w-lg max-h-[85dvh] p-0">
            <div className="flex flex-col max-h-[85dvh]">
              <DialogHeader className="p-4 pb-2 shrink-0">
                <DialogTitle className="font-heading text-lg text-[#1e3a5f] flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Calendar Preferences
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Choose events, visibility, and reminder times
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto px-4 pb-2">
                <div className="space-y-4 py-2">
                  {/* Daily Tasks Visibility */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Daily Tasks</h3>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-800">Show on Team Calendar</p>
                          <p className="text-xs text-blue-600 mt-0.5">
                            Share your daily task progress with the team as a single consolidated entry
                          </p>
                        </div>
                        <Switch
                          checked={preferences.show_daily_on_team || false}
                          onCheckedChange={(checked) => setPreferences(p => ({ ...p, show_daily_on_team: checked }))}
                        />
                      </div>
                    </div>
                  </div>

              {/* Event Types - Compact for mobile */}
              <div className="space-y-2">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Events to Include</h3>
                
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { key: 'include_tasks', label: 'Tasks', color: 'bg-amber-500' },
                    { key: 'include_followups', label: 'Follow-ups', color: 'bg-violet-500' },
                    { key: 'include_quotes', label: 'Quote Deadlines', color: 'bg-red-500' },
                    { key: 'include_enquiries', label: 'Milestones', color: 'bg-pink-500' },
                    { key: 'include_approvals', label: 'Approvals', color: 'bg-emerald-500' },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${item.color}`} />
                        <span className="text-sm text-slate-700">{item.label}</span>
                      </div>
                      <Switch
                        checked={preferences[item.key]}
                        onCheckedChange={(checked) => setPreferences(p => ({ ...p, [item.key]: checked }))}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Reminder Times - Compact grid */}
              <div className="space-y-2">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Reminders</h3>
                
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'reminder_minutes_tasks', label: 'Tasks' },
                    { key: 'reminder_minutes_followups', label: 'Follow-ups' },
                    { key: 'reminder_minutes_deadlines', label: 'Deadlines' },
                    { key: 'reminder_minutes_milestones', label: 'Milestones' },
                    { key: 'reminder_minutes_approvals', label: 'Approvals' },
                    { key: 'days_ahead', label: 'Days Ahead', isDays: true },
                  ].map(item => (
                    <div key={item.key}>
                      <Label className="text-xs text-slate-500">{item.label}</Label>
                      <Select
                        value={String(preferences[item.key])}
                        onValueChange={(v) => setPreferences(p => ({ ...p, [item.key]: parseInt(v) }))}
                      >
                        <SelectTrigger className="rounded-lg mt-1 h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {item.isDays ? (
                            <>
                              <SelectItem value="7">7 days</SelectItem>
                              <SelectItem value="14">14 days</SelectItem>
                              <SelectItem value="30">30 days</SelectItem>
                              <SelectItem value="60">60 days</SelectItem>
                            </>
                          ) : (
                            REMINDER_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
                </div>
              </div>
            
              <DialogFooter className="p-4 pt-2 shrink-0 border-t gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreferencesModal(false)}
                  className="rounded-lg"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={savePreferences}
                  disabled={savingPrefs}
                  className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-lg"
                >
                  {savingPrefs ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Calendar Subscription Modal */}
        <CalendarSubscriptionDialog 
          open={showSubscriptionModal} 
          onOpenChange={setShowSubscriptionModal}
        />
      </MainLayout>
    );
  }

  // Desktop Calendar View
  return (
    <MainLayout>
      <div className="space-y-4 md:space-y-6 w-full min-w-0 max-w-full overflow-x-hidden" data-testid="calendar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
              Calendar
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Manage tasks, follow-ups, and deadlines
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowPreferencesModal(true)}
              variant="outline"
              className="rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50"
              data-testid="calendar-preferences-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Alerts
            </Button>
            <Button
              onClick={() => setShowSubscriptionModal(true)}
              variant="outline"
              className="rounded-xl border-blue-200 text-blue-700 hover:bg-blue-50"
              data-testid="calendar-subscribe-btn"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Export/Sync
            </Button>
            <Button
              onClick={() => {
                setFormData({
                  title: '',
                  description: '',
                  due_date: new Date().toISOString().split('T')[0],
                  due_time: '',
                  all_day: true,
                  category: 'outreach',
                  status: 'pending'
                });
                setShowCreateModal(true);
              }}
              className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
              data-testid="new-task-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Task
            </Button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-2 sm:gap-3 p-3 bg-white rounded-xl border border-slate-100">
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
            <span className="text-slate-600">Pending</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
            <span className="text-slate-600">Done</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-2.5 h-2.5 rounded-full bg-violet-500" />
            <span className="text-slate-600">Follow-ups</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span className="text-slate-600">Quote Due</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <div className="w-2.5 h-2.5 rounded-full bg-pink-500" />
            <span className="text-slate-600">Deadline</span>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-2 sm:p-4 overflow-x-auto">
          <div className="calendar-wrapper min-w-0">
            <style>{`
            .fc {
              font-family: inherit;
              --fc-border-color: #e2e8f0;
              max-width: 100%;
            }
            .fc .fc-toolbar-title {
              font-size: 1rem;
              font-weight: 600;
              color: #1e3a5f;
            }
            .fc .fc-button {
              background-color: #f1f5f9;
              border: none;
              color: #475569;
              font-weight: 500;
              padding: 0.35rem 0.7rem;
              font-size: 0.75rem;
              border-radius: 0.5rem;
            }
            .fc .fc-button:hover {
              background-color: #e2e8f0;
            }
            .fc .fc-button-primary:not(:disabled).fc-button-active {
              background-color: #1e3a5f;
              color: white;
            }
            .fc .fc-daygrid-day-number {
              color: #475569;
              font-weight: 500;
              font-size: 0.8rem;
              padding: 4px 8px;
            }
            .fc .fc-daygrid-day.fc-day-today {
              background-color: #eff6ff !important;
            }
            .fc .fc-event {
              border-radius: 4px;
              padding: 2px 4px;
              font-size: 0.7rem;
              cursor: pointer;
              margin: 1px 2px;
            }
            .fc .fc-event:hover {
              opacity: 0.85;
            }
            .fc-theme-standard td, .fc-theme-standard th {
              border-color: #e2e8f0;
            }
            .fc-theme-standard .fc-scrollgrid {
              border-color: #e2e8f0;
            }
            .fc .fc-col-header-cell-cushion {
              color: #64748b;
              font-weight: 600;
              font-size: 0.7rem;
              text-transform: uppercase;
              padding: 8px 4px;
            }
            .fc .fc-daygrid-body,
            .fc .fc-scrollgrid-sync-table {
              width: 100% !important;
            }
            .fc .fc-daygrid-day-frame {
              min-height: 80px;
            }
            .fc .fc-more-link {
              color: #1e3a5f;
              font-weight: 500;
            }
            .fc-direction-ltr .fc-daygrid-event.fc-event-end {
              margin-right: 2px;
            }
            .fc-direction-ltr .fc-daygrid-event.fc-event-start {
              margin-left: 2px;
            }
            /* Ensure scrollgrid doesn't overflow container */
            .fc .fc-scrollgrid {
              width: 100% !important;
              table-layout: fixed !important;
            }
            .fc .fc-scrollgrid-section > * {
              width: 100% !important;
            }
            /* Critical: Force view harness to have height */
            .fc .fc-view-harness {
              min-height: 450px !important;
              height: auto !important;
            }
            .fc .fc-view-harness-active {
              min-height: 450px !important;
            }
            .fc .fc-scroller {
              overflow: visible !important;
            }
            .fc .fc-scroller-liquid-absolute {
              position: relative !important;
            }
            /* Mobile fixes */
            @media (max-width: 640px) {
              .fc .fc-toolbar {
                flex-direction: column;
                gap: 0.5rem;
              }
              .fc .fc-toolbar-chunk {
                display: flex;
                justify-content: center;
                flex-wrap: wrap;
              }
              .fc .fc-toolbar-title {
                font-size: 0.9rem;
              }
              .fc .fc-button {
                padding: 0.25rem 0.5rem;
                font-size: 0.7rem;
              }
              .fc .fc-daygrid-day-number {
                font-size: 0.7rem;
                padding: 2px 4px;
              }
              .fc .fc-event {
                font-size: 0.6rem;
                padding: 1px 2px;
              }
              .fc .fc-daygrid-day-frame {
                min-height: 45px;
              }
              .fc .fc-col-header-cell-cushion {
                font-size: 0.6rem;
                padding: 4px 2px;
              }
              /* Critical mobile grid fix */
              .fc .fc-view-harness {
                min-height: 350px !important;
              }
              .fc .fc-daygrid-body {
                width: 100% !important;
              }
              .fc .fc-scrollgrid-sync-table {
                width: 100% !important;
              }
              .fc table {
                font-size: 0.7rem;
                width: 100% !important;
                table-layout: fixed !important;
              }
              .fc .fc-scroller-harness {
                overflow: visible !important;
              }
              .fc .fc-daygrid {
                width: 100% !important;
              }
              /* Fix for mobile grid body not showing */
              .fc .fc-scroller-liquid-absolute {
                position: relative !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: auto !important;
              }
              .fc .fc-daygrid-body-balanced {
                width: 100% !important;
              }
              .fc-scrollgrid-section-body > td {
                border-bottom: none;
              }
              .fc .fc-scrollgrid-section-body table {
                height: auto !important;
                min-height: 250px;
              }
              /* Mobile List View Improvements */
              .fc .fc-list {
                border: none;
              }
              .fc .fc-list-event {
                cursor: pointer;
              }
              .fc .fc-list-event-title {
                font-size: 0.85rem;
              }
              .fc .fc-list-day-cushion {
                background: #f8fafc;
                padding: 8px 12px;
                font-weight: 600;
              }
              .fc .fc-list-event-time {
                font-size: 0.75rem;
                color: #64748b;
              }
              /* Hide week view button on mobile */
              .fc .fc-timeGridWeek-button {
                display: none;
              }
            }
          `}</style>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
            initialView={typeof window !== 'undefined' && window.innerWidth < 768 ? 'listWeek' : 'dayGridMonth'}
            locale={fullCalendarLocale.locale}
            firstDay={fullCalendarLocale.firstDay}
            eventTimeFormat={fullCalendarLocale.eventTimeFormat}
            slotLabelFormat={fullCalendarLocale.slotLabelFormat}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: typeof window !== 'undefined' && window.innerWidth < 768 
                ? 'listWeek,dayGridMonth' 
                : 'dayGridMonth,timeGridWeek,listWeek'
            }}
            views={{
              listWeek: {
                buttonText: 'List',
                listDayFormat: { weekday: 'short', day: 'numeric', month: 'short' },
                noEventsText: 'No events this week'
              },
              dayGridMonth: {
                buttonText: 'Month'
              },
              timeGridWeek: {
                buttonText: 'Week'
              }
            }}
            events={events}
            editable={true}
            droppable={true}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : 2}
            weekends={true}
            datesSet={handleDatesSet}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            eventDrop={handleEventDrop}
            aspectRatio={typeof window !== 'undefined' && window.innerWidth < 768 ? 1.0 : 1.35}
            expandRows={true}
            eventDisplay="block"
            displayEventTime={false}
            nowIndicator={true}
            stickyHeaderDates={false}
            handleWindowResize={true}
            contentHeight={typeof window !== 'undefined' && window.innerWidth < 768 ? 'auto' : undefined}
          />
          </div>
        </div>

        {/* Tips */}
        <div className="text-xs text-slate-400 text-center">
          💡 Click a date to add a task • Drag events to reschedule • Click an event to edit
        </div>

        {/* Event Detail/Edit Modal */}
        <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-md max-h-[90dvh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-base">
                {selectedEvent?.type === 'task' && <Target className="w-5 h-5 text-amber-500" />}
                {selectedEvent?.type === 'followup' && <Users className="w-5 h-5 text-violet-500" />}
                {selectedEvent?.type === 'quote_deadline' && <FileText className="w-5 h-5 text-red-500" />}
                {selectedEvent?.type === 'enquiry_deadline' && <AlertTriangle className="w-5 h-5 text-pink-500" />}
                {selectedEvent?.type === 'task' ? 'Edit Task' : 'Event Details'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {selectedEvent?.type === 'task' ? (
                <>
                  {/* Editable Task Form */}
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500">Title</Label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="mt-1"
                        data-testid="edit-task-title"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-xs text-slate-500">Description</Label>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="mt-1"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Date</Label>
                        <Input
                          type="date"
                          value={formData.due_date}
                          onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Category</Label>
                        <Select 
                          value={formData.category} 
                          onValueChange={(v) => setFormData({ ...formData, category: v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORY_OPTIONS.map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Status Toggle */}
                    <button
                      onClick={handleToggleComplete}
                      className={`w-full flex items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                        formData.status === 'completed'
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <CheckCircle2 className={`w-5 h-5 ${formData.status === 'completed' ? 'text-emerald-500' : 'text-slate-400'}`} />
                      {formData.status === 'completed' ? 'Completed' : 'Mark as Complete'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Read-only Event Info */}
                  <div className="space-y-3">
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <p className="font-medium text-slate-800">{selectedEvent?.title}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        {selectedEvent?.start && new Date(selectedEvent.start).toLocaleDateString('en-GB', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                      {/* Creator Attribution */}
                      {selectedEvent?.created_by && (
                        <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                          <User className="w-3 h-3" />
                          Created by {selectedEvent.created_by}
                        </p>
                      )}
                    </div>
                    
                    {selectedEvent?.type === 'followup' && (
                      <div className="p-3 bg-violet-50 rounded-xl">
                        <p className="text-sm text-violet-700">
                          <strong>Contact:</strong> {selectedEvent.contact_name}
                        </p>
                        {selectedEvent.company_name && (
                          <p className="text-sm text-violet-600 mt-1">
                            {selectedEvent.company_name}
                          </p>
                        )}
                      </div>
                    )}

                    {selectedEvent?.type === 'outreach' && (
                      <div className="p-3 bg-cyan-50 rounded-xl space-y-2">
                        <p className="text-sm text-cyan-700">
                          <strong>Method:</strong> {selectedEvent.method?.charAt(0).toUpperCase() + selectedEvent.method?.slice(1)}
                        </p>
                        {selectedEvent.contact_name && (
                          <p className="text-sm text-cyan-600">
                            <strong>Contact:</strong> {selectedEvent.contact_name}
                          </p>
                        )}
                        {selectedEvent.outcome && (
                          <p className="text-sm text-cyan-600">
                            <strong>Outcome:</strong> {selectedEvent.outcome}
                          </p>
                        )}
                        {selectedEvent.notes && (
                          <p className="text-sm text-cyan-600 mt-2">
                            {selectedEvent.notes}
                          </p>
                        )}
                      </div>
                    )}

                    {selectedEvent?.type === 'daily_summary' && (
                      <div className="p-3 bg-blue-50 rounded-xl space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-blue-700">Daily Progress</span>
                          <span className={`text-sm font-bold ${
                            selectedEvent.percentage === 100 ? 'text-emerald-600' : 'text-blue-600'
                          }`}>
                            {selectedEvent.completed_tasks}/{selectedEvent.total_tasks} ({selectedEvent.percentage}%)
                          </span>
                        </div>
                        {/* Progress bar */}
                        <div className="w-full bg-blue-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              selectedEvent.percentage === 100 ? 'bg-emerald-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${selectedEvent.percentage}%` }}
                          />
                        </div>
                        {/* Task list - only show for user's own summary */}
                        {selectedEvent.is_own && selectedEvent.tasks && selectedEvent.tasks.length > 0 && (
                          <div className="space-y-1 pt-2 border-t border-blue-200">
                            <p className="text-xs text-blue-600 font-medium">Tasks:</p>
                            {selectedEvent.tasks.map((task, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs text-blue-700">
                                {task.status === 'completed' ? (
                                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                ) : (
                                  <div className="w-3 h-3 rounded-full border border-blue-400" />
                                )}
                                <span className={task.status === 'completed' ? 'line-through opacity-60' : ''}>
                                  {task.title}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Note for team member summaries */}
                        {!selectedEvent.is_own && (
                          <p className="text-xs text-blue-500 italic pt-2 border-t border-blue-200">
                            Task details are private to {selectedEvent.created_by}
                          </p>
                        )}
                      </div>
                    )}

                    {selectedEvent?.type === 'quote_deadline' && (
                      <div className="p-3 bg-red-50 rounded-xl">
                        <p className="text-sm text-red-700">
                          Quote expires on this date. Review and follow up if needed.
                        </p>
                      </div>
                    )}

                    {selectedEvent?.type === 'enquiry_deadline' && (
                      <div className="p-3 bg-pink-50 rounded-xl">
                        <p className="text-sm text-pink-700">
                          <strong>Project:</strong> {selectedEvent.project_name}
                        </p>
                      </div>
                    )}

                    {(selectedEvent?.type === 'followup' || selectedEvent?.type === 'quote_deadline' || selectedEvent?.type === 'enquiry_deadline') && (
                      <Button
                        onClick={handleNavigateToItem}
                        className="w-full bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                    )}

                    {selectedEvent?.type === 'outreach' && selectedEvent?.contact_id && (
                      <Button
                        onClick={() => navigate(`/pipeline?contact=${selectedEvent.contact_id}`)}
                        className="w-full bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Contact
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>

            {selectedEvent?.type === 'task' && (selectedEvent?.can_edit || selectedEvent?.can_delete) && (
              <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
                {selectedEvent?.can_delete && (
                  <Button
                    variant="outline"
                    onClick={handleDeleteTask}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
                {selectedEvent?.can_edit && (
                  <Button
                    onClick={handleUpdateTask}
                    className="bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                  >
                    Save Changes
                  </Button>
                )}
              </DialogFooter>
            )}
            
            {selectedEvent?.type === 'task' && !selectedEvent?.can_edit && !selectedEvent?.can_delete && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                <p>This task belongs to {selectedEvent?.created_by}. Only the owner or a Director can edit/delete it.</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create Task Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-[95vw] sm:max-w-md p-0 max-h-[85dvh] flex flex-col">
            <DialogHeader className="p-4 sm:p-6 pb-0 flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#1e3a5f]" />
                Create New Task
              </DialogTitle>
              <DialogDescription>
                {formData.due_date && `For ${new Date(formData.due_date).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}`}
              </DialogDescription>
            </DialogHeader>

            {/* Scrollable form content */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-2" style={{ minHeight: '200px', maxHeight: 'calc(85dvh - 180px)' }}>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Enter task title"
                    className="mt-1"
                    data-testid="new-task-title"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description"
                    className="mt-1"
                    rows={2}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Date</Label>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Category</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Time-based scheduling with Duration */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <input
                      type="checkbox"
                      id="all-day"
                      checked={formData.all_day}
                      onChange={(e) => setFormData({ ...formData, all_day: e.target.checked, due_time: '', duration_minutes: 60, end_time: '' })}
                      className="w-4 h-4 rounded border-slate-300 text-[#1e3a5f] focus:ring-[#1e3a5f]"
                    />
                    <label htmlFor="all-day" className="text-sm text-slate-700 cursor-pointer">
                      All day task
                    </label>
                  </div>
                  
                  {!formData.all_day && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        {/* Start Time */}
                        <div>
                          <Label className="text-xs text-slate-500">Start Time</Label>
                          <Input
                            type="time"
                            value={formData.due_time}
                            onChange={(e) => setFormData({ ...formData, due_time: e.target.value })}
                            className="mt-1"
                            data-testid="task-time-input"
                          />
                        </div>
                        
                        {/* Duration Dropdown */}
                        <div>
                          <Label className="text-xs text-slate-500">Duration</Label>
                          <Select 
                            value={String(formData.duration_minutes)} 
                            onValueChange={(v) => setFormData({ ...formData, duration_minutes: Number(v) })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select duration" />
                            </SelectTrigger>
                            <SelectContent>
                              {DURATION_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Custom End Time - only shown when Custom is selected */}
                      {formData.duration_minutes === 0 && (
                        <div>
                          <Label className="text-xs text-slate-500">End Time</Label>
                          <Input
                            type="time"
                            value={formData.end_time}
                            onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                            className="mt-1"
                          />
                        </div>
                      )}
                      
                      {/* Calculated Time Display */}
                      {formData.duration_minutes !== 0 && formData.due_time && (
                        <div className="p-2 bg-blue-50 rounded-lg flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          <p className="text-sm text-blue-700">
                            {formData.due_time} — {formData.end_time || calculateEndTime(formData.due_time, formData.duration_minutes)}
                            <span className="text-blue-500 ml-2">
                              ({DURATION_OPTIONS.find(o => o.value === formData.duration_minutes)?.label})
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Bottom padding */}
                <div className="h-2" />
              </div>
            </div>

            {/* Sticky footer with CTA buttons */}
            <DialogFooter className="flex-shrink-0 p-4 sm:p-6 pt-3 border-t border-slate-100 bg-white gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCreateModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTask}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white"
                data-testid="create-task-submit"
              >
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notification Preferences Modal */}
        <Dialog open={showPreferencesModal} onOpenChange={setShowPreferencesModal}>
          <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#1e3a5f] flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Calendar Preferences
              </DialogTitle>
              <DialogDescription>
                Choose which events appear and manage team visibility
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Daily Tasks Visibility - NEW */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">Daily Tasks</h3>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-blue-800">Show on Team Calendar</p>
                      <p className="text-xs text-blue-600 mt-1">
                        Share your daily task progress with the team as a single consolidated entry (e.g., "John's Tasks: 8/12 completed")
                      </p>
                    </div>
                    <Switch
                      checked={preferences.show_daily_on_team || false}
                      onCheckedChange={(checked) => setPreferences(p => ({ ...p, show_daily_on_team: checked }))}
                      data-testid="pref-show-daily-on-team"
                    />
                  </div>
                </div>
              </div>

              {/* Event Types */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">Event Types</h3>
                
                {/* Tasks */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <div>
                      <p className="font-medium text-slate-800">Tasks</p>
                      <p className="text-xs text-slate-500">Daily routine tasks and to-dos</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.include_tasks}
                    onCheckedChange={(checked) => setPreferences(p => ({ ...p, include_tasks: checked }))}
                    data-testid="pref-tasks-toggle"
                  />
                </div>
                
                {/* Follow-ups */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-violet-500" />
                    <div>
                      <p className="font-medium text-slate-800">Follow-ups</p>
                      <p className="text-xs text-slate-500">Contact follow-up reminders</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.include_followups}
                    onCheckedChange={(checked) => setPreferences(p => ({ ...p, include_followups: checked }))}
                    data-testid="pref-followups-toggle"
                  />
                </div>
                
                {/* Deadlines (Quotes) */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <div>
                      <p className="font-medium text-slate-800">Quote Deadlines</p>
                      <p className="text-xs text-slate-500">Quote submission deadlines</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.include_quotes}
                    onCheckedChange={(checked) => setPreferences(p => ({ ...p, include_quotes: checked }))}
                    data-testid="pref-quotes-toggle"
                  />
                </div>
                
                {/* Milestones (Enquiries) */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-pink-500" />
                    <div>
                      <p className="font-medium text-slate-800">Project Milestones</p>
                      <p className="text-xs text-slate-500">Project start and end dates</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.include_enquiries}
                    onCheckedChange={(checked) => setPreferences(p => ({ ...p, include_enquiries: checked }))}
                    data-testid="pref-enquiries-toggle"
                  />
                </div>
                
                {/* Approvals */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <div>
                      <p className="font-medium text-slate-800">Approvals</p>
                      <p className="text-xs text-slate-500">Pending approval reminders</p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences.include_approvals}
                    onCheckedChange={(checked) => setPreferences(p => ({ ...p, include_approvals: checked }))}
                    data-testid="pref-approvals-toggle"
                  />
                </div>
              </div>
              
              {/* Reminder Times */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">Reminder Times</h3>
                <p className="text-xs text-slate-500">Set how far in advance you want to be reminded for each event type</p>
                
                {/* Task reminders */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Tasks</Label>
                    <Select
                      value={String(preferences.reminder_minutes_tasks)}
                      onValueChange={(v) => setPreferences(p => ({ ...p, reminder_minutes_tasks: parseInt(v) }))}
                    >
                      <SelectTrigger className="rounded-xl mt-1" data-testid="reminder-tasks-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REMINDER_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-600">Follow-ups</Label>
                    <Select
                      value={String(preferences.reminder_minutes_followups)}
                      onValueChange={(v) => setPreferences(p => ({ ...p, reminder_minutes_followups: parseInt(v) }))}
                    >
                      <SelectTrigger className="rounded-xl mt-1" data-testid="reminder-followups-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REMINDER_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-600">Quote Deadlines</Label>
                    <Select
                      value={String(preferences.reminder_minutes_deadlines)}
                      onValueChange={(v) => setPreferences(p => ({ ...p, reminder_minutes_deadlines: parseInt(v) }))}
                    >
                      <SelectTrigger className="rounded-xl mt-1" data-testid="reminder-deadlines-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REMINDER_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-600">Project Milestones</Label>
                    <Select
                      value={String(preferences.reminder_minutes_milestones)}
                      onValueChange={(v) => setPreferences(p => ({ ...p, reminder_minutes_milestones: parseInt(v) }))}
                    >
                      <SelectTrigger className="rounded-xl mt-1" data-testid="reminder-milestones-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REMINDER_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-600">Approvals</Label>
                    <Select
                      value={String(preferences.reminder_minutes_approvals)}
                      onValueChange={(v) => setPreferences(p => ({ ...p, reminder_minutes_approvals: parseInt(v) }))}
                    >
                      <SelectTrigger className="rounded-xl mt-1" data-testid="reminder-approvals-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REMINDER_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-slate-600">Days Ahead</Label>
                    <Select
                      value={String(preferences.days_ahead)}
                      onValueChange={(v) => setPreferences(p => ({ ...p, days_ahead: parseInt(v) }))}
                    >
                      <SelectTrigger className="rounded-xl mt-1" data-testid="days-ahead-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                        <SelectItem value="60">60 days</SelectItem>
                        <SelectItem value="90">90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPreferencesModal(false)}
                className="rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={savePreferences}
                disabled={savingPrefs}
                className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
                data-testid="save-preferences-btn"
              >
                {savingPrefs ? 'Saving...' : 'Save Preferences'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Calendar Subscription Modal */}
        <CalendarSubscriptionDialog 
          open={showSubscriptionModal} 
          onOpenChange={setShowSubscriptionModal}
        />
      </div>
    </MainLayout>
  );
};

export default CalendarPage;
